import React, { useState, useEffect } from 'react';

import { ClickAwayListener } from '@material-ui/core';
import _ from 'lodash';
import { parse, isToday, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import './Calendar.scss';
import { marks } from '../../../utils/content';
import Icon from '../../Icon/Icon';
import CalendarEvents from './CalendarEvents';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/configs/store';
import useIsMobile from 'src/hooks/useIsMobile';

interface CalendarProps {
  eventType: string,
  getTreeData: Function,
  allowFilter?: boolean,
  type: string
}

function Calendar({ eventType, getTreeData, allowFilter=false, type }: CalendarProps) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarType, setCalendarType] = useState('');
  const [activeDate, setActiveDate] = useState(0);
  const mobile = useIsMobile();

  const dispatch = useDispatch();
  const { dataTree, filterSlider } = useSelector((state: RootState) => state.events);
  const { optionsType, product, firstProduct } = useSelector((state: RootState) => state.filters);

  const history = useHistory();
  const { t } = useTranslation();

  const defineTodaysDate = () => {
    return format(new Date(), 'yyyyMMdd');
  };


  const toggleCalendaList = (value: boolean) => {
    let selectedCountries = [];
    if (value){
      const tree = _.isArray(dataTree.tree) ? dataTree.tree[0]?.categories : Object.values(dataTree.tree);
      selectedCountries= _.map(tree, (item) => item.code);
    }
    dispatch.filters.updateSelectedCountries(selectedCountries);
  };

  const formatDate = (date: string, dateFormat: string) => {
    if (date) {
      // The date from the URL is number and the date coming from backend is a string
      return format(parse(date, (_.isNumber(date) ? 'yyyyMMdd' : 'yyyy.MM.dd'), new Date()), dateFormat);
    }
    return null;
  };

  const setDefaultFilterToday = async () => {
    const date = defineTodaysDate();

    await dispatch.events.changeFilterSlider({
      range: 4,
      rangeHours: 24,
      selectedDate: date
    });
  };

  useEffect(() => {
    if (dataTree.tree && firstProduct){
      toggleCalendaList(true);
      dispatch.filters.deactivateFirstProduct();
    }
  }, [dataTree]);

  useEffect(() => {
    if (history.action !== 'PUSH' || product !== type){
      const query = new URLSearchParams(location.search);
      const date = query.get('calendar');
      if ((!query.get('range') && !query.get('calendar'))) {
        setDefaultFilterToday();
        dispatch.filters.updateCalendarType('calendar');
      } else {
        if (query.get('range')) {
          const range = Number(query.get('range'));
          const currentMark = _.find(marks.numbersBetting, m => m.hours === range);

          dispatch.events.changeFilterSlider({
            range: currentMark.value,
            rangeHours: currentMark.hours
          });
        }

        if (query.get('calendar') !== 'all') {
          dispatch.filters.updateCalendarType('calendar');
          setCalendarType(null);
          dispatch.events.changeFilterSlider({
            selectedDate: date,
            rangeHours: isToday(parse(date, 'yyyyMMdd', new Date())) ? filterSlider.rangeHours : '-1'
          });
        }

        if (query.get('calendar') === 'all') {
          dispatch.filters.updateCalendarType('all');
          setCalendarType('all');
          dispatch.events.changeFilterSlider({
            rangeHours: '-1',
            selectedDate: ''
          });
          getTreeData({ mode: 'antepost' });
          return ;
        }
      }
      getTreeData();
    }
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('calendar') === 'all') {
      setCalendarType('all');
    } else if (query.get('calendar') && dataTree?.calendarInfo){

      const selectedIndex = _.findIndex(Object.values(dataTree.calendarInfo), (info) => {
        return formatDate(info['debugInfoDate'], 'yyyyMMdd') === query.get('calendar');
      });

      setActiveDate(selectedIndex);
    }
  }, [history]);

  const changePrevDate = () => {
    const query = new URLSearchParams(location.search);
    setCalendarType(null);

    if (activeDate > 0) {
      const formattedDate = formatDate(Object.values(dataTree.calendarInfo)[activeDate - 1]['debugInfoDate'], 'yyyyMMdd');

      setActiveDate(1);
      setCalendarOpen(false);
      dispatch.events.changeFilterSlider({
        range: 0,
        rangeHours: activeDate < 0 ? 0 : '-1',
        selectedDate: formattedDate
      });

      if (!mobile){
        history.replace({
          pathname: location.pathname,
          search: eventType === 'racing' ? `?type=${ query.get('type') || '' }&calendar=${ formattedDate }` : `?calendar=${ formattedDate }`
        });
      }
      getTreeData();
    }
  };

  const toggleCalendarAllTab = () => {
    dispatch.events.resetTree();
    const query = new URLSearchParams(location.search);
    dispatch.filters.updateCalendarType('all');
    setCalendarType('all');
    dispatch.events.changeFilterSlider({
      rangeHours: '-1',
      selectedDate: ''
    });

    if (!mobile){
      history.replace({
        pathname: location.pathname,
        search: eventType === 'racing' ? `?type=${ query.get('type') || '' }&calendar=all` : '&calendar=all'
      });
    }
    getTreeData({ mode: 'antepost' });
  };

  const changeNextDate = () => {
    const query = new URLSearchParams(location.search);
    const calendarInfo = dataTree?.calendarInfo ? Object.values(dataTree.calendarInfo) : [];
    setCalendarType(null);

    if (activeDate < calendarInfo.length - 1) {
      const formattedDate = formatDate(calendarInfo[activeDate + 1]['debugInfoDate'], 'yyyyMMdd');

      setActiveDate(activeDate + 1);
      dispatch.events.changeFilterSlider({
        range: 0,
        rangeHours: activeDate < 0 ? 0 : '-1',
        selectedDate: formattedDate
      });
      if (!mobile){
        history.replace({
          pathname: location.pathname,
          search: eventType === 'racing' ? `?type=${ query.get('type') || '' }&calendar=${ formattedDate }` : `?calendar=${ formattedDate }`
        });
      }
      getTreeData();
    }
  };

  const changeDate = (selectedDate: { debugInfoDate?: string }, index: number) => {
    dispatch.events.resetTree();
    const formattedDate = formatDate(selectedDate.debugInfoDate, 'yyyyMMdd');
    const query = new URLSearchParams(location.search);

    setActiveDate(+index);
    setCalendarOpen(false);
    setCalendarType(null);
    dispatch.filters.updateCalendarType('calendar');
    dispatch.events.changeFilterSlider({
      range: 0,
      rangeHours: index > 0 ? '-1' : 0,
      selectedDate: formattedDate
    });
    if (!mobile){
      history.replace({
        pathname: location.pathname,
        search: eventType === 'racing' ? `?type=${ query.get('type') || '' }&calendar=${ formattedDate }` : `?calendar=${ formattedDate }`
      });
    }
    getTreeData();
  };

  const renderCalendarEvents = () => {
    const noOptionsSelected = !optionsType?.horses && !optionsType?.dogs && !optionsType?.carriages;

    if (!_.isEmpty(dataTree) && !noOptionsSelected) {
      const tree = _.isArray(dataTree.tree) ? dataTree.tree[0]?.categories : Object.values(dataTree.tree);
      const filteredTree = _.map(tree, (item) => {
        const category = JSON.parse(JSON.stringify(item));
        if (allowFilter) {
          const filteredTournaments = _.filter(category.tournaments, (tournament) => {
            if ((tournament.sportCode === 202 || tournament.sportCode === 201) && optionsType.horses === true) {
              return true;
            } else if (tournament.sportCode === 203 && optionsType.carriages === true) {
              return true;
            } else if (tournament.sportCode === 204 && optionsType.dogs === true) {
              return true;
            } else {
              return false;
            }
          });

          if (filteredTournaments.length === 0) {
            return null;
          } else {
            category.tournaments = filteredTournaments;
          }
        }

        return (
          <CalendarEvents
            key={ category.name }
            event={ category }
            eventType={ eventType }
            isSameDay={ activeDate === 0 }
            calendarType={ calendarType }
          />
        );
      });

      if (_.compact(filteredTree).length === 0) {
        return (<div>{ t('events.noEvents') }</div>);
      }

      return filteredTree;
    }
    return (<div>{ t('events.noEvents') }</div>);
  };

  return (
    <div className="calendar">
      <ClickAwayListener onClickAway={ () => setCalendarOpen(false) }>
        <div className="calendar__header">
          <div className="calendar__type">
            {
              <div className={
                classNames({
                  'calendar__actions': true,
                  'calendar__actions--open': calendarOpen,
                  'calendar__actions--not-active':  calendarType === 'all'
                })
              }
              >
                <button className="calendar__btn calendar__btn--prev" onClick={ changePrevDate }>
                  <Icon icon="arrow" />
                </button>
                <span className="calendar__date" onClick={ () => setCalendarOpen(!calendarOpen) }>
                  {
                    calendarType !== 'all' &&
                    dataTree.calendarInfo &&
                    formatDate(Object.values(dataTree.calendarInfo)[activeDate]?.['debugInfoDate'], 'MMM dd')
                  }
                  <Icon icon="calendar" />
                </span>
                <button
                  className="calendar__btn calendar__btn--next"
                  onClick={ changeNextDate }
                >
                  <Icon icon="arrow" />
                </button>
                <ul className={
                  classNames({
                    'calendar__date__list': true,
                    'calendar__date__list--active': calendarOpen
                  })
                }
                >
                  {
                    dataTree.calendarInfo && _.map(Object.values(dataTree.calendarInfo), (date, index) => {
                      return <li className="calendar__date__item" key={ date['debugInfoDate'] }>
                        <button
                          className="calendar__date__btn"
                          onClick={ () => changeDate(date, index) }
                        >
                          { formatDate(date['debugInfoDate'], 'ccc MMM dd') }
                        </button>
                      </li>;
                    })
                  }
                </ul>
              </div>
            }
            <div
              className={
                classNames({
                  'calendar__type__btn': true,
                  'calendar__type__btn--active':  calendarType === 'all'
                })
              }
              onClick={ toggleCalendarAllTab }
            >
              { eventType === 'number' ? t('calendar.all') : t('calendar.antepost') }
            </div>
          </div>
        </div>
      </ClickAwayListener>
      <div className="calendar__buttons">
        <button
          className="calendar__button"
          onClick={ () => toggleCalendaList(true) }
        >
          { t('racingDetail.openAll') }
        </button>
        <button
          className="calendar__button"
          onClick={ () => toggleCalendaList(false) }
        >
          { t('racingDetail.closeAll') }
        </button>
      </div>
      <div className="calendar__body">
        { renderCalendarEvents() }
      </div>
    </div>
  );
}

export default Calendar;
